// src/components/CreateDartPopup.js
import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle } from "@headlessui/react";
import { FaRegQuestionCircle, FaTimes } from "react-icons/fa";
import { motion } from "framer-motion";

const CreateDartPopup = ({
  isOpen,
  isEditing = false,
  closePopup,
  name,
  setName,
  weight,
  setWeight,
  description,
  setDescription,
  shortCode,
  setShortCode,
  stem,
  setStem,
  flight,
  setFlight,
  isDefault,
  setIsDefault,
  onSave,
}) => {
  const [isSaving, setIsSaving] = useState(false);

  // Ensure form fields reflect editing initial values
  useEffect(() => {
    // no-op: parent drives field values
  }, [isOpen]);

  const isValid =
    name.trim() !== "" && weight !== "" && shortCode.trim() !== "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      alert("Please fill in Name, Weight, and Short Code.");
      return;
    }
    setIsSaving(true);
    try {
      await onSave({
        name,
        weight: Number(weight),
        description,
        shortCode,
        stem,
        flight,
        isDefault,
      });
      // parent will close and reset
    } catch (err) {
      console.error(err);
      alert("Failed to save dart.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={closePopup} className="relative z-50">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="relative w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 300,
              duration: 0.3,
            }}
            className="bg-gradient-to-br from-secondary to-secondary/80 p-6 rounded-xl shadow-2xl border border-light/20 backdrop-blur-sm"
          >
            {/* Close X */}
            <button
              onClick={closePopup}
              className="absolute top-6 right-6 p-2 text-gray-400 hover:text-test transition-colors duration-200"
            >
              <FaTimes size={20} />
            </button>

            {/* Title */}
            <DialogTitle className="text-2xl font-bold mb-6 text-test text-center flex items-center justify-center">
              {isEditing ? "Edit Dart" : "Create New Dart"}
              <span className="relative group ml-2">
                <FaRegQuestionCircle className="text-test" />
                <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-primary text-white text-sm p-2 rounded shadow-lg">
                  <strong>Info</strong>
                  <p className="mt-1">
                    Customize your dart properties to track performance.
                  </p>
                </div>
              </span>
            </DialogTitle>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-white">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-primary/80 border border-light/30 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-test transition-all duration-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-white">
                    Short Code *
                  </label>
                  <input
                    type="text"
                    value={shortCode}
                    onChange={(e) => setShortCode(e.target.value)}
                    className="w-full bg-primary/80 border border-light/30 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-test transition-all duration-200"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-white">
                    Stem
                  </label>
                  <input
                    type="text"
                    value={stem}
                    onChange={(e) => setStem(e.target.value)}
                    className="w-full bg-primary/80 border border-light/30 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-test transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-white">
                    Flight
                  </label>
                  <input
                    type="text"
                    value={flight}
                    onChange={(e) => setFlight(e.target.value)}
                    className="w-full bg-primary/80 border border-light/30 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-test transition-all duration-200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-white">
                    Weight (g) *
                  </label>
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="w-full bg-primary/80 border border-light/30 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-test transition-all duration-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-white">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full bg-primary/80 border border-light/30 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-test transition-all duration-200"
                    rows="3"
                  />
                </div>
              </div>

              <div className="bg-primary/30 p-4 rounded-lg border border-light/20">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                    className="h-5 w-5 text-test rounded accent-test focus:ring-2 focus:ring-test/50"
                  />
                  <label className="ml-3 text-sm font-medium text-white">
                    Set as default dart
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  type="submit"
                  disabled={!isValid || isSaving}
                  className="px-6 py-2 bg-gradient-to-br from-test to-test/80 text-primary font-medium rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving
                    ? isEditing
                      ? "Updating..."
                      : "Saving..."
                    : isEditing
                    ? "Update"
                    : "Save"}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default CreateDartPopup;
